/**
 * iDiamonds — linii-taxă legate de un produs părinte.
 *
 * O linie-taxă (garanție, gravură) poartă proprietatea ascunsă `_pentru_varianta`
 * cu id-ul variantei pe care o însoțește. Modulul face două lucruri:
 *
 * 1. Expune `window.iDiamonds.linkedItems.add()` — singurul loc din temă care
 *    adaugă o linie-taxă în coș.
 * 2. Reconciliază coșul la fiecare `cartUpdate` și la încărcarea paginii:
 *    - taxa dispare când produsul părinte nu mai e în coș;
 *    - taxa nu depășește niciodată cantitatea totală a produsului părinte.
 *
 * Reconcilierea plafonează **doar în jos**, niciodată în sus. Motivul e o
 * proprietate a Shopify, nu o preferință: două linii de coș se contopesc când au
 * aceeași variantă, aceleași proprietăți și același selling plan. Linia-părinte
 * nu poartă niciun marcaj de garanție (marcajul stă pe linia-taxă), deci a doua
 * adăugare a aceluiași produs — chiar cu „garanție standard" aleasă — se
 * contopește în linia existentă. Dacă taxa ar urca odată cu cantitatea
 * contopită, clientul ar plăti o garanție pe care tocmai a refuzat-o, fără s-o
 * fi cerut și fără s-o vadă. Diferența neacoperită se oferă din nou, explicit,
 * prin rândul de upsell din coș (`snippets/warranty-upsell.liquid`), care apare
 * cât timp sunt mai puține garanții decât bucăți.
 *
 * Cât timp un PUT de reconciliere e deja în curs, ce sosește peste el nu se
 * pierde — dar nici nu se reține. Din evenimentul suprapus păstrăm doar
 * intenția: un semnal boolean, `needsRecheck`. Când PUT-ul se termină (cu
 * succes sau cu eșec), semnalul ridicat declanșează o recitire a coșului de la
 * server (`reconcileFromServer()`), iar reconcilierea se face pe starea aia.
 *
 * De ce intenție și nu instantaneu de coș: orice instantaneu — payload-ul unui
 * eveniment, răspunsul unui PUT, rezultatul unui `fetchCart()` — poate fi deja
 * învechit față de o mutație venită pe altă cerere HTTP (alt tab, cart-drawer,
 * un `add()` concurent), iar două citiri separate n-au între ele nicio garanție
 * de ordonare. Reținând doar semnalul, nu mai există niciun instantaneu de
 * suprascris: reluarea lucrează mereu pe adevărul serverului din momentul
 * reluării. Costul e un `GET /cart.js` în plus per reluare coalescată.
 *
 * Ecoul propriu al lui `reconcile()` poartă `source: SOURCE` și e ignorat de
 * abonatul propriu — nu ca să protejeze vreo stare (nu mai e nimic de
 * protejat), ci ca ecoul fiecărui PUT să nu ceară o recitire inutilă a coșului.
 * Ecoul lui `add()` NU e marcat: `add()` e singurul loc care poate produce o
 * linie-taxă pentru un părinte care n-a ajuns niciodată în coș
 * (`product-ajax:added` e global, iar quick-view-ul poate ține vii simultan
 * două `<warranty-extension>` pentru produse diferite — vezi
 * `warranty-extension.js`), deci rezultatul lui trebuie să ajungă la
 * `reconcile()`, ca orfanul să fie curățat imediat, fără niciun eveniment
 * extern ulterior. NU marca și publicarea din `add()` cu `source`.
 *
 * Ceilalți abonați din temă citesc doar `event.cart`, iar `source` e deja o
 * convenție existentă în temă (vezi `quick-order-list.js`), deci n-are alt
 * efect secundar.
 *
 * Reîncercările auto-declanșate sunt plafonate — vezi
 * `MAX_CONSECUTIVE_ATTEMPTS` — pentru cazul (rar) în care evenimente externe
 * repetate tot nu ajung la un coș consistent.
 *
 * Fără localStorage: în preview/theme editor storefront-ul rulează în iframe
 * unde localStorage poate fi blocat.
 */
const LINK_PROPERTY = '_pentru_varianta';

/** Discriminant pus pe propriile publicări `cartUpdate`, ca abonatul propriu să le recunoască și să le ignore. */
const SOURCE = 'cart-linked-items';

/**
 * Câte reconcilieri consecutive pot găsi coșul tot inconsistent înainte să renunțăm,
 * în loc să retrimitem PUT-uri la nesfârșit. Contorul numără doar reconcilieri care
 * **nu au convers**: o reconciliere al cărei PUT lasă coșul consistent își resetează
 * singură contorul, pe răspunsul PUT-ului (vezi `reconcile()`). Fără asta, plafonul
 * ar deveni un contor pe toată durata sesiunii — ecoul propriu al PUT-ului e filtrat
 * de abonat, iar nimic altceva din temă nu republică `cartUpdate` cu coșul consistent,
 * deci observația „acum e consistent" n-ar mai veni niciodată. Șase click-uri pe
 * stepper-ul unei linii cu garanție ar fi epuizat plafonul și ar fi oprit inclusiv
 * ștergerea liniilor orfane.
 *
 * 5 lasă loc confortabil peste orice serie legitimă, dar tot eșuează rapid — nu zeci
 * de runde — când neconcordanța chiar persistă (plafon de inventar, alt script pe
 * aceleași linii): acolo PUT-ul „reușește" HTTP fără să convergă, deci contorul urcă.
 *
 * Odată depășit pragul, eroarea se loghează o singură dată (nu la fiecare încercare
 * respinsă) și reconcilierea rămâne oprită până când un coș găsit consistent
 * (`computeUpdates` întoarce `{}`) resetează contorul — nu se resetează la momentul
 * plafonării, altfel un reset imediat ar permite câte un PUT nou la fiecare a șasea
 * încercare, adică exact bucla nemărginită pe care pragul trebuie s-o oprească.
 */
const MAX_CONSECUTIVE_ATTEMPTS = 5;

/** Secțiunile pe care coșul vrea să le re-randeze, ca în gift-wrapping.js. */
function groupedSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
  );
  return sections;
}

class CartLinkedItems {
  constructor() {
    this.isReconciling = false;
    // A sosit ceva cât eram ocupați? Doar atât — nu și ce anume. Vezi reconcile().
    this.needsRecheck = false;
    // Câte reconcilieri la rând au găsit coșul tot inconsistent — vezi MAX_CONSECUTIVE_ATTEMPTS.
    this.consecutiveAttempts = 0;

    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Ecoul propriu al lui reconcile() — îl ignorăm ca să nu ceară o recitire
      // a coșului după fiecare PUT. Ecoul lui add() nu e marcat și trece
      // (vezi antetul fișierului).
      if (event && event.source === SOURCE) return;
      this.reconcile(event && event.cart);
    });

    this.reconcileFromServer();
  }

  /** Adaugă o linie-taxă și anunță tema. Întoarce coșul actualizat. */
  async add({ variantId, quantity, properties }) {
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity, properties }],
      sections: groupedSections(),
      sections_url: window.location.pathname,
    });

    const response = await fetch(FoxTheme.routes.cart_add_url, {
      ...FoxTheme.utils.fetchConfig(),
      body,
    });
    const added = await response.json();
    if (added.status) {
      const error = new Error(added.description || added.message || 'Adăugare eșuată');
      // Payload-ul brut al refuzului (status, message, errors pe câmpuri) rămâne
      // atașat erorii — altfel se pierde tot detaliul la conversia în `Error`, iar
      // apelanții (warranty-extension.js, custom-engraving.js) n-au ce loga la debug.
      error.payload = added;
      throw error;
    }

    const cart = await this.fetchCart();
    cart.sections = added.sections;
    // FĂRĂ `source` — vezi antetul fișierului. Rezultatul lui add() trebuie să
    // ajungă la reconcile(), altfel o linie-taxă al cărei părinte n-a intrat de
    // fapt în coș rămâne orfană până la un eveniment extern care s-ar putea să
    // nu mai vină (client → checkout direct din drawer).
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    return cart;
  }

  async fetchCart() {
    const response = await fetch(FoxTheme.routes.cart_url, {
      ...FoxTheme.utils.fetchConfig('json', 'GET'),
    });
    return response.json();
  }

  async reconcileFromServer() {
    try {
      this.reconcile(await this.fetchCart());
    } catch (error) {
      console.error('Linii legate — nu am putut citi coșul:', error);
    }
  }

  /**
   * Calculează modificările necesare: fiecare linie-taxă e plafonată la cantitatea
   * totală a produsului părinte. Doar în jos — vezi antetul fișierului. Când
   * părintele lipsește cu totul, totalul e 0, deci `Math.min` duce taxa la 0.
   * @returns {Object} map de line item key -> cantitate nouă; gol dacă e consistent.
   */
  computeUpdates(items) {
    const parentQuantity = new Map();

    items.forEach((item) => {
      if (item.properties && item.properties[LINK_PROPERTY]) return;
      const key = String(item.variant_id);
      parentQuantity.set(key, (parentQuantity.get(key) || 0) + item.quantity);
    });

    const updates = {};
    items.forEach((item) => {
      const parentId = item.properties && item.properties[LINK_PROPERTY];
      if (!parentId) return;

      const target = Math.min(item.quantity, parentQuantity.get(String(parentId)) || 0);
      if (target !== item.quantity) updates[item.key] = target;
    });

    return updates;
  }

  async reconcile(cart) {
    if (this.isReconciling) {
      // O reconciliere e deja în curs (PUT către cart/update.js în zbor). Nu
      // pornim un al doilea PUT concurent și nu reținem coșul primit — ar fi un
      // instantaneu care poate fi deja învechit față de altă sursă. Reținem doar
      // intenția: mai e nevoie de o verificare. `finally` o onorează recitind
      // coșul de la server, deci reluarea lucrează pe adevărul serverului, nu pe
      // un payload de care nu știm cât e de recent.
      this.needsRecheck = true;
      return;
    }
    if (!cart || !Array.isArray(cart.items)) return;

    const updates = this.computeUpdates(cart.items);
    if (Object.keys(updates).length === 0) {
      // Coș consistent — orice serie de încercări blocate anterior se resetează aici,
      // nu la momentul plafonării (altfel un reset imediat ar permite un PUT nou la
      // fiecare a șasea încercare, adică exact bucla nemărginită pe care vrem s-o oprim).
      this.consecutiveAttempts = 0;
      return;
    }

    this.consecutiveAttempts += 1;
    if (this.consecutiveAttempts === MAX_CONSECUTIVE_ATTEMPTS + 1) {
      // Logăm o singură dată, la tranziție — nu la fiecare încercare respinsă ulterior.
      console.error(
        `Linii legate — opresc după ${MAX_CONSECUTIVE_ATTEMPTS} încercări consecutive fără convergență; coșul poate rămâne inconsistent.`
      );
    }
    if (this.consecutiveAttempts > MAX_CONSECUTIVE_ATTEMPTS) return;

    this.isReconciling = true;
    try {
      const body = JSON.stringify({
        updates,
        sections: groupedSections(),
        sections_url: window.location.pathname,
      });
      const response = await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      });
      const updated = await response.json();
      // A convers? Atunci încercarea asta nu contează la plafon. Recalculăm pe
      // răspunsul PUT-ului, nu pe un eveniment ulterior: ecoul propriu e filtrat de
      // abonat, iar nimic altceva din temă nu republică `cartUpdate` cu coșul
      // consistent (`CartItems.onCartUpdate` doar re-randează HTML), deci fără asta
      // contorul ar deveni unul pe toată durata sesiunii. Un coș rămas inconsistent
      // *după* PUT continuă să numere — exact scopul gărzii.
      if (
        updated &&
        Array.isArray(updated.items) &&
        Object.keys(this.computeUpdates(updated.items)).length === 0
      ) {
        this.consecutiveAttempts = 0;
      }
      // CU `source` — spre deosebire de add() mai sus (vezi antetul fișierului):
      // abonatul propriu îl ignoră, ca ecoul fiecărui PUT să nu ceară o recitire
      // a coșului. Un eveniment extern real, sosit cât timp PUT-ul era în zbor,
      // a ridicat deja `needsRecheck` — ăla declanșează recitirea, nu ecoul.
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: updated, source: SOURCE });
    } catch (error) {
      console.error('Linii legate — reconciliere eșuată:', error);
    } finally {
      this.isReconciling = false;
      // A sosit ceva cât eram ocupați (cu succes sau cu eșec — garda tot se
      // eliberează mai sus)? Nu reluăm pe un coș memorat, ci recitim coșul de la
      // server și reconciliem pe el. computeUpdates() de la începutul metodei
      // oprește lanțul natural când starea proaspătă e deja consistentă
      // (întoarce {}), iar MAX_CONSECUTIVE_ATTEMPTS îl oprește când nu converge.
      if (this.needsRecheck) {
        this.needsRecheck = false;
        this.reconcileFromServer();
      }
    }
  }
}

window.iDiamonds = window.iDiamonds || {};
if (!window.iDiamonds.linkedItems) {
  window.iDiamonds.linkedItems = new CartLinkedItems();
}
